import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import {
  financeService,
  ENTITY_TYPE_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  MEMBERSHIP_TYPE_OPTIONS,
  ATTACHMENT_TYPE_OPTIONS,
} from '../../services/financeService';
import { financeEntityService } from '../../services/financeEntityService';
import { TANZANIA_REGION_OPTIONS, getRegionLabel } from '../../utils/tanzaniaRegions';

const EMPTY_RECORD = {
  entityType: 'player',
  entityId: '',
  entityName: '',
  licenseNumber: '',
  region: '',
  membershipType: 'annual_license',
  amount: '',
  currency: 'TZS',
  paymentStatus: 'pending',
  paymentMethod: '',
  paymentReference: '',
  receiptNumber: '',
  paidAt: '',
  dueDate: '',
  seasonYear: String(new Date().getFullYear()),
  notes: '',
};

const MembershipRecords = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_RECORD);
  const [isSaving, setIsSaving] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentType, setAttachmentType] = useState('receipt');
  const [formError, setFormError] = useState(null);
  const [entityOptions, setEntityOptions] = useState([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await financeService.getMembershipRecords({
        entityType: filterEntityType || undefined,
        paymentStatus: filterStatus || undefined,
      });
      setRecords(data);
    } catch (err) {
      setError(err.message || 'Failed to load membership records');
    } finally {
      setIsLoading(false);
    }
  }, [filterEntityType, filterStatus]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) =>
      [r.entityName, r.licenseNumber, r.receiptNumber, r.paymentReference, getRegionLabel(r.region)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [records, search]);

  useEffect(() => {
    if (!modalOpen || form.entityType === 'other') {
      setEntityOptions([]);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingEntities(true);

    financeEntityService
      .getOptions(form.entityType)
      .then((options) => {
        if (!cancelled) setEntityOptions(options);
      })
      .catch((err) => {
        if (!cancelled) setFormError(err.message || 'Failed to load entities');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEntities(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, form.entityType]);

  const entitySelectOptions = useMemo(
    () =>
      financeEntityService.withSavedEntity(entityOptions, {
        entityId: form.entityId,
        entityName: form.entityName,
        licenseNumber: form.licenseNumber,
        region: form.region,
      }),
    [entityOptions, form.entityId, form.entityName, form.licenseNumber, form.region]
  );

  const handleEntityTypeChange = (entityType) => {
    setForm((prev) => ({
      ...prev,
      entityType,
      entityId: '',
      entityName: '',
      licenseNumber: '',
      region: '',
    }));
    setFormError(null);
  };

  const handleEntitySelect = (entityId) => {
    const selected = entitySelectOptions.find((option) => option.value === entityId);
    if (!selected) {
      setForm((prev) => ({ ...prev, entityId: entityId || '', entityName: '' }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      entityId: selected.entity.id,
      entityName: selected.entity.name,
      licenseNumber: selected.entity.licenseNumber || prev.licenseNumber,
      region: selected.entity.region || prev.region,
    }));
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setForm(EMPTY_RECORD);
    setAttachmentFile(null);
    setFormError(null);
    setEntityOptions([]);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    setForm({
      entityType: record.entityType,
      entityId: record.entityId || '',
      entityName: record.entityName || '',
      licenseNumber: record.licenseNumber || '',
      region: record.region || '',
      membershipType: record.membershipType || 'annual_license',
      amount: String(record.amount ?? ''),
      currency: record.currency || 'TZS',
      paymentStatus: record.paymentStatus || 'pending',
      paymentMethod: record.paymentMethod || '',
      paymentReference: record.paymentReference || '',
      receiptNumber: record.receiptNumber || '',
      paidAt: record.paidAt ? record.paidAt.split('T')[0] : '',
      dueDate: record.dueDate || '',
      seasonYear: record.seasonYear || String(new Date().getFullYear()),
      notes: record.notes || '',
    });
    setAttachmentFile(null);
    setFormError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (form.entityType !== 'other' && !form.entityId) {
      setFormError('Please select an entity');
      return;
    }
    if (!form.entityName?.trim()) {
      setFormError('Entity name is required');
      return;
    }
    setIsSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount || 0),
        paidAt: form.paidAt ? new Date(form.paidAt).toISOString() : null,
      };

      let saved;
      if (editingRecord) {
        saved = await financeService.updateMembershipRecord(editingRecord.id, payload);
      } else {
        saved = await financeService.createMembershipRecord(payload);
      }

      if (attachmentFile) {
        await financeService.uploadAttachment(saved.id, attachmentFile, attachmentType);
        saved = (await financeService.getMembershipRecords()).find((r) => r.id === saved.id) || saved;
      }

      setModalOpen(false);
      await loadRecords();
    } catch (err) {
      setFormError(err.message || 'Failed to save record');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete membership record for ${record.entityName}?`)) return;
    try {
      await financeService.deleteMembershipRecord(record.id);
      await loadRecords();
    } catch (err) {
      setError(err.message || 'Failed to delete record');
    }
  };

  const handleDeleteAttachment = async (record, attachment) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await financeService.deleteAttachment(attachment.id, attachment.filePath);
      await loadRecords();
      if (editingRecord?.id === record.id) {
        const updated = (await financeService.getMembershipRecords()).find((r) => r.id === record.id);
        if (updated) openEditModal(updated);
      }
    } catch (err) {
      setFormError(err.message || 'Failed to delete attachment');
    }
  };

  const formatCurrency = (amount, currency = 'TZS') =>
    new Intl.NumberFormat('en-TZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount || 0);

  const statusClass = (status) => {
    if (status === 'paid') return 'text-success';
    if (status === 'overdue') return 'text-destructive';
    if (status === 'partial' || status === 'pending') return 'text-warning';
    return 'text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: 'Finance', path: '/finance-dashboard' },
            { label: 'Membership Records' },
          ]} />

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Receipt" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Membership & License Records</h1>
                <p className="text-muted-foreground">
                  Payments, receipts, and attachments for players, coaches, officials, and clubs
                </p>
              </div>
            </div>
            <Button iconName="Plus" onClick={openCreateModal}>New Record</Button>
          </div>

          {error && (
            <div className="p-4 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input label="Search" placeholder="Name, license, receipt..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select label="Entity Type" placeholder="All types" options={[{ value: '', label: 'All types' }, ...ENTITY_TYPE_OPTIONS]} value={filterEntityType} onChange={setFilterEntityType} />
            <Select label="Payment Status" placeholder="All statuses" options={[{ value: '', label: 'All statuses' }, ...PAYMENT_STATUS_OPTIONS]} value={filterStatus} onChange={setFilterStatus} />
            <div className="flex items-end">
              <Button variant="outline" className="w-full" iconName="RefreshCw" onClick={loadRecords} disabled={isLoading}>
                Refresh
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : filteredRecords.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No membership records found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-4 py-3">Entity</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">License</th>
                      <th className="px-4 py-3">Region</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Attachments</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record) => (
                      <tr key={record.id} className="border-b border-border/60 hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">{record.entityName}</td>
                        <td className="px-4 py-3 capitalize">{record.entityType?.replace('_', ' ')}</td>
                        <td className="px-4 py-3">{record.licenseNumber || '—'}</td>
                        <td className="px-4 py-3">{getRegionLabel(record.region) || '—'}</td>
                        <td className="px-4 py-3">{formatCurrency(record.amount, record.currency)}</td>
                        <td className={`px-4 py-3 capitalize font-medium ${statusClass(record.paymentStatus)}`}>
                          {record.paymentStatus?.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3">{record.attachments?.length || 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(record)} title="Edit">
                              <Icon name="Edit" size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(record)} title="Delete">
                              <Icon name="Trash2" size={14} className="text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4 card-shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingRecord ? 'Edit Record' : 'New Membership Record'}</h2>
              <Button variant="ghost" size="sm" onClick={() => setModalOpen(false)}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            {formError && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">{formError}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Entity Type"
                options={ENTITY_TYPE_OPTIONS}
                value={form.entityType}
                onChange={handleEntityTypeChange}
                required
              />
              {form.entityType === 'other' ? (
                <Input
                  label="Entity Name"
                  value={form.entityName}
                  onChange={(e) => setForm((p) => ({ ...p, entityId: '', entityName: e.target.value }))}
                  required
                />
              ) : (
                <Select
                  label="Entity Name"
                  placeholder={isLoadingEntities ? 'Loading entities...' : 'Select entity'}
                  options={entitySelectOptions}
                  value={form.entityId || form.entityName || ''}
                  onChange={handleEntitySelect}
                  searchable
                  clearable
                  loading={isLoadingEntities}
                  required
                />
              )}
              <Input label="License Number" value={form.licenseNumber} onChange={(e) => setForm((p) => ({ ...p, licenseNumber: e.target.value }))} />
              <Select label="Region" placeholder="Select region" options={TANZANIA_REGION_OPTIONS} value={form.region} onChange={(v) => setForm((p) => ({ ...p, region: v }))} />
              <Select label="Membership Type" options={MEMBERSHIP_TYPE_OPTIONS} value={form.membershipType} onChange={(v) => setForm((p) => ({ ...p, membershipType: v }))} />
              <Input label="Amount (TZS)" type="number" min="0" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
              <Select label="Payment Status" options={PAYMENT_STATUS_OPTIONS} value={form.paymentStatus} onChange={(v) => setForm((p) => ({ ...p, paymentStatus: v }))} />
              <Input label="Payment Method" value={form.paymentMethod} onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))} placeholder="Bank transfer, cash, mobile money" />
              <Input label="Payment Reference" value={form.paymentReference} onChange={(e) => setForm((p) => ({ ...p, paymentReference: e.target.value }))} />
              <Input label="Receipt Number" value={form.receiptNumber} onChange={(e) => setForm((p) => ({ ...p, receiptNumber: e.target.value }))} />
              <Input label="Paid Date" type="date" value={form.paidAt} onChange={(e) => setForm((p) => ({ ...p, paidAt: e.target.value }))} />
              <Input label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))} />
              <Input label="Season Year" value={form.seasonYear} onChange={(e) => setForm((p) => ({ ...p, seasonYear: e.target.value }))} />
            </div>

            <Input label="Notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />

            {editingRecord?.attachments?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Existing Attachments</p>
                {editingRecord.attachments.map((att) => (
                  <div key={att.id} className="flex items-center justify-between p-2 bg-muted/30 rounded border border-border text-sm">
                    <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                      {att.fileName || att.attachmentType}
                    </a>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteAttachment(editingRecord, att)}>
                      <Icon name="Trash2" size={14} className="text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-4">
              <Select label="Attachment Type" options={ATTACHMENT_TYPE_OPTIONS} value={attachmentType} onChange={setAttachmentType} />
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Upload Receipt / Document</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary/10 file:text-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
              <Button loading={isSaving} iconName="Save" onClick={handleSave}>
                {editingRecord ? 'Update Record' : 'Create Record'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipRecords;
